import { memo, useCallback, useState } from 'react';

import { useTrackActivatedIntercom } from '@air/analytics';
import { LockKeyholeIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { Portal } from '@air/primitive-portal';
import { Spinner } from '@air/primitive-spinner';
import { ONE_HOUR } from '@air/utilities';

import AirStripe from '~/components/AirStripe';
import { PaymentUpdateForm } from '~/components/WorkspaceLockedOverlay/PaymentUpdateForm';
import { WorkspaceSwitcherButton } from '~/components/WorkspaceLockedOverlay/WorkspaceSwitcherButton';
import { WORKSPACE_LOCKED_PAYMENT_UPDATED } from '~/constants/localStorageKeys';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubscription } from '~/swr-hooks/subscriptions/useSubscription';

/** A payment update within this many hours suppresses the locked-workspace overlay. */
const HOURS_TREATED_AS_RECENT_PAYMENT = 24;

type WorkspaceLockedOverlayProps = {
  children: JSX.Element;
};

export const WorkspaceLockedOverlay = memo(({ children }: WorkspaceLockedOverlayProps) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: subscription } = useSubscription();
  const { trackActivatedIntercom } = useTrackActivatedIntercom();
  const [showPaymentView, setShowPaymentView] = useState(false);

  const isWorkspaceLocked = currentWorkspace?.status === 'locked';

  const isOnEnterprisePlan = subscription?.isOnEnterprisePlan;
  const isBasic = !subscription?.subscribed || subscription?.planId === 'basic';

  // Check if payment was recently updated (within last 24 hours)
  const hasRecentlyUpdatedPayment = (() => {
    if (!currentWorkspace?.id) {
      return false;
    }

    const storageKey = `${WORKSPACE_LOCKED_PAYMENT_UPDATED}/${currentWorkspace.id}`;
    const paymentUpdateTime = localStorage.getItem(storageKey);

    if (!paymentUpdateTime) {
      return false;
    }

    const updateDate = new Date(paymentUpdateTime);
    const hoursSinceUpdate = (Date.now() - updateDate.getTime()) / ONE_HOUR;

    return hoursSinceUpdate < HOURS_TREATED_AS_RECENT_PAYMENT;
  })();

  const handleContactSupport = useCallback(() => {
    trackActivatedIntercom({ location: 'main-nav' });
    if (window.Intercom) {
      window.Intercom('show');
    }
  }, [trackActivatedIntercom]);

  const handleUpdatePayment = useCallback(() => {
    setShowPaymentView(true);
  }, []);

  const handleBackToLocked = useCallback(() => {
    setShowPaymentView(false);
  }, []);

  if (!isWorkspaceLocked) {
    return children;
  }

  return (
    <>
      {children}
      <Portal>
        {/* Transparent gray backdrop */}
        <div className="fixed inset-0 z-portal bg-black/40">
          <div className="flex size-full items-center justify-center">
            <div className="relative w-full max-w-[520px] overflow-hidden rounded-20 shadow-2xl">
              {/* Top section with peach background and centered lock */}
              <div className="flex items-center justify-center ads:bg-warning bg-orange-2 py-10">
                <div className="flex size-20 items-center justify-center">
                  <LockKeyholeIcon className="size-[50px] ads:text-icon-warning text-orange-9" />
                </div>
              </div>

              {/* Bottom section with content */}
              <div className="bg-white p-8 dark:bg-black">
                {!showPaymentView ? (
                  <>
                    {/* Title - left aligned, 24px */}
                    <h1 className="mb-3 font-semibold ads:text-primary text-24 text-gray-12">
                      Update your billing information
                    </h1>

                    {/* Description - left aligned, 16px */}
                    <p className="mb-6 ads:text-secondary text-16 text-gray-11 leading-relaxed">
                      {hasRecentlyUpdatedPayment ? (
                        <>
                          Your payment information was recently updated. Please{' '}
                          <button
                            type="button"
                            onClick={handleContactSupport}
                            className="ads:text-secondary text-gray-11 underline ads:hover:text-primary hover:text-gray-12"
                          >
                            contact
                          </button>{' '}
                          our support team for assistance with reactivating your workspace.
                        </>
                      ) : (
                        <>
                          Your workspace is paused because payment is past due. To regain access, please{' '}
                          <button
                            type="button"
                            onClick={handleContactSupport}
                            className="ads:text-secondary text-gray-11 underline ads:hover:text-primary hover:text-gray-12"
                          >
                            contact
                          </button>{' '}
                          our support team.
                        </>
                      )}
                    </p>

                    {/* Buttons - justify between */}
                    <div className="flex justify-between gap-3">
                      <Button
                        onClick={
                          isOnEnterprisePlan || isBasic || hasRecentlyUpdatedPayment
                            ? handleContactSupport
                            : handleUpdatePayment
                        }
                        appearance="filled"
                      >
                        {isOnEnterprisePlan || isBasic || hasRecentlyUpdatedPayment
                          ? 'Contact support'
                          : 'Update payment info'}
                      </Button>
                      <WorkspaceSwitcherButton />
                    </div>
                  </>
                ) : (
                  <AirStripe>
                    {({ stripeIsLoaded }) =>
                      stripeIsLoaded ? (
                        <PaymentUpdateForm onBack={handleBackToLocked} />
                      ) : (
                        <div className="flex h-[200px] items-center justify-center">
                          <Spinner className="size-7 ads:text-ads-cyan-10 text-teal-9" />
                        </div>
                      )
                    }
                  </AirStripe>
                )}
              </div>
            </div>
          </div>
        </div>
      </Portal>
    </>
  );
});

WorkspaceLockedOverlay.displayName = 'WorkspaceLockedOverlay';
